/**
 * The 429 envelope for a refused account-merge code send (#1163, vingapi#1666 S2).
 *
 *   POST /api/users/merge/{id}/verify/
 *     -> 429 {"detail": {"error": "verification_rate_limited",
 *                        "message": "...", "retry_after": <int>}}
 *        + Retry-After: <int>
 *
 * A send budget is spent (the requester's, the method's or the merge's).
 * Nothing was sent, and a code an earlier press sent stays valid, so this is
 * not a dead end: AccountMergeDialog shows the code step and says when
 * another code can be sent.
 *
 * Like `contactMethodClaim.ts`, the envelope is absent from vingapi's OpenAPI
 * document (FastAPI does not emit exception bodies), so this reader IS the
 * contract. It fails closed: anything other than status 429 with
 * `detail.error === 'verification_rate_limited'` yields null and the caller
 * keeps its ordinary error handling.
 */
import { AxiosError } from 'axios';
import { z } from 'zod';
import { unwrapConflictDetail } from './contactMethodClaim';

export const MERGE_SEND_RATE_LIMITED = 'verification_rate_limited';

const MergeSendRateLimitSchema = z.object({
  error: z.literal(MERGE_SEND_RATE_LIMITED),
});

/** Seconds are only ever a non-negative integer; anything else is ignored. */
const SecondsSchema = z.number().int().nonnegative();

export interface MergeSendRateLimit {
  /** Seconds until another code can be sent, when the server said; else null. */
  retryAfterSeconds: number | null;
}

/** `Retry-After` as delta-seconds (a non-negative integer); an HTTP-date or junk is null. */
function retryAfterHeader(error: AxiosError): number | null {
  const raw: unknown = error.response?.headers?.['retry-after'];
  if (typeof raw !== 'string' || !/^\d+$/.test(raw.trim())) return null;
  return Number(raw.trim());
}

/**
 * Is this send-code failure the budget refusal? Returns when another code can
 * be sent: `detail.retry_after` (either key case: the detail is camel-cased
 * by `unwrapConflictDetail`), else the `Retry-After` header, else null.
 */
export function parseMergeSendRateLimit(error: unknown): MergeSendRateLimit | null {
  const detail = unwrapConflictDetail(error, 429);
  if (detail === null || !(error instanceof AxiosError)) return null;
  if (!MergeSendRateLimitSchema.safeParse(detail).success) return null;
  const fromDetail = SecondsSchema.safeParse((detail as { retryAfter?: unknown }).retryAfter);
  return { retryAfterSeconds: fromDetail.success ? fromDetail.data : retryAfterHeader(error) };
}
